import React, { useState, useEffect, useRef } from 'react';
import { Edit, X, User, Phone, FileText, CreditCard, MapPin, Users, Pencil, Download } from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import ProfileImageUpload from '../component/ProfileImageUpload';
import IdentityCardFormat from '../component/IdentityCardFormat';
import Navbar from './Navbar';
import { toast } from 'react-hot-toast';
import ReactDOM from 'react-dom/client';


const INDIAN_BANKS = [
  { name: 'State Bank of India', color: '#1a4b8c' },
  { name: 'HDFC Bank', color: '#004c8f' },
  { name: 'ICICI Bank', color: '#f58220' },
  { name: 'Axis Bank', color: '#97144d' },
  { name: 'Kotak Mahindra Bank', color: '#ed1c24' },
  { name: 'Punjab National Bank', color: '#1e3a8a' },
  { name: 'Bank of Baroda', color: '#f15a22' },
  { name: 'Canara Bank', color: '#ffd700' },
  { name: 'Union Bank of India', color: '#f26522' },
  { name: 'Bank of India', color: '#f15a29' },
  { name: 'Indian Bank', color: '#00529b' },
  { name: 'Central Bank of India', color: '#e31837' },
  { name: 'Indian Overseas Bank', color: '#0066b3' },
  { name: 'UCO Bank', color: '#6b2c91' },
  { name: 'Bank of Maharashtra', color: '#00a651' },
  { name: 'Punjab & Sind Bank', color: '#8b0000' },
  { name: 'IDBI Bank', color: '#00a651' },
  { name: 'Yes Bank', color: '#0066b3' },
  { name: 'IndusInd Bank', color: '#8b0000' },
  { name: 'Federal Bank', color: '#003366' },
  { name: 'South Indian Bank', color: '#0066b3' },
  { name: 'RBL Bank', color: '#e31837' },
  { name: 'Bandhan Bank', color: '#f15a22' },
  { name: 'IDFC First Bank', color: '#9c1d26' },
  { name: 'Karur Vysya Bank', color: '#00529b' },
  { name: 'City Union Bank', color: '#0066b3' },
  { name: 'Tamilnad Mercantile Bank', color: '#1e3a8a' },
  { name: 'DCB Bank', color: '#e31837' },
  { name: 'Dhanlaxmi Bank', color: '#00a651' },
  { name: 'Karnataka Bank', color: '#1e3a8a' },
  { name: 'Jammu & Kashmir Bank', color: '#003366' },
];

const MyProfile = () => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [originalProfileData, setOriginalProfileData] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fullUserData, setFullUserData] = useState(null);
  const fileInputRef = useRef(null);
  const cardContainerRef = useRef(null);
  const [profileData, setProfileData] = useState({
    gender: '',
    mobile_number: '',
    country_code: '+91',
    date_of_birth: '',
    pan_card_number: '',
    personal_email: '',
    emergency_contact_no: '',
    mother_name: '',
    father_name: '',
    permanent_address: '',
    current_address: '',
    first_name: '',
    last_name: '',
    country: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    bank_name: '',
    aadhar_number: '',
    aadhar_name: '',
    designation_id_FK: '',
    department_id_FK: '',
    blood_group: ''
  });

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("userId");
      if (!token || !storedUserId) return;

      const response = await axiosInstance.get(`/api/users/${storedUserId}`, {
        headers: { Authorization: `Bearer ${token.trim()}` }
      });

      if (response.data?.status && response.data?.data) {
        const userData = response.data.data;
        const data = {
          gender: userData.gender || '',
          mobile_number: userData.mobile_number || '',
          country_code: userData.country_code || '+91',
          date_of_birth: userData.date_of_birth ? userData.date_of_birth.split('T')[0] : '',
          pan_card_number: userData.pan_card_number || '',
          personal_email: userData.personal_email || '',
          emergency_contact_no: userData.emergency_contact_no || '',
          mother_name: userData.mother_name || '',
          father_name: userData.father_name || '',
          permanent_address: userData.permanent_address || '',
          current_address: userData.current_address || '',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          country: userData.country || '',
          account_number: userData.account_number || '',
          confirm_account_number: userData.account_number || '',
          ifsc_code: userData.ifsc_code || '',
          bank_name: userData.bank_name || '',
          aadhar_number: userData.aadhar_number || '',
          aadhar_name: userData.aadhar_name || '',
          designation_id_FK: userData.designation_id_FK || '',
          department_id_FK: userData.department_id_FK || '',
          blood_group: userData.blood_group || ''
        };
        setProfileData(data);
        setOriginalProfileData(data);
        setFullUserData(userData); // Store full user data for ID card

        // Store profile image URL
        if (userData.user_profile_img) {
          const imageUrl = userData.user_profile_img.startsWith('http')
            ? userData.user_profile_img
            : `${axiosInstance.defaults.baseURL}${userData.user_profile_img.startsWith('/') ? '' : '/'}${userData.user_profile_img}`;
          setProfileImageUrl(imageUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUserId = localStorage.getItem("userId");
    const role = localStorage.getItem("role");

    if (!token || !storedUserId) {
      setLoading(false);
      return;
    }

    fetchProfileData();

    if (role === '3' || role === 3) {
      const profileCompleted = localStorage.getItem('profileCompleted');
      if (!profileCompleted) {
        setShowProfileModal(true);
      }
    }
  }, []);

  const handleOpenProfileModal = () => {
    fetchProfileData();
    setShowProfileModal(true);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const hasDbValue = (fieldName) => {
    return originalProfileData[fieldName] && originalProfileData[fieldName] !== '';
  };

  const getInputClass = (fieldName) => {
    const baseClass = "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
    return hasDbValue(fieldName)
      ? `${baseClass} border-green-400 bg-green-50`
      : `${baseClass} border-gray-300`;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    if (profileData.account_number && profileData.account_number !== profileData.confirm_account_number) {
      toast.error('Account numbers do not match!');
      return;
    }

    setProfileLoading(true);
    try {
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("userId");

      const { confirm_account_number, ...dataToSend } = profileData;

      const filteredData = Object.fromEntries(
        Object.entries(dataToSend).filter(([_, v]) => v !== '' && v !== null)
      );

      await axiosInstance.patch(`/api/users/${storedUserId}`, filteredData, {
        headers: {
          Authorization: `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Profile updated successfully!');
      localStorage.setItem('profileCompleted', 'true');
      setShowProfileModal(false);
      fetchProfileData();
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleImageFileChange = async (event) => {
    console.log('handleImageFileChange called', event.target.files);
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      toast.error('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));

    // Upload immediately
    console.log('Starting upload for file:', file.name);
    await handleImageUpload(file);
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      console.error('No file provided to handleImageUpload');
      return;
    }

    console.log('handleImageUpload called with file:', file.name);

    const formData = new FormData();
    formData.append("image", file);
    console.log('FormData created, file appended');

    try {
      setUploadingImage(true);
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("userId");

      console.log('Token:', token ? 'Present' : 'Missing', 'UserId:', storedUserId);

      if (!token || !storedUserId) {
        toast.error('Authentication required');
        return;
      }

      console.log('Sending request to:', `/api/users/edit_profile_picture/${storedUserId}`);
      console.log('FormData entries:', Array.from(formData.entries()));
      console.log('File details:', { name: file.name, type: file.type, size: file.size });

      // Use axios directly with FormData - it will automatically set Content-Type with boundary
      // Match the curl command exactly: Post /api/users/edit_profile_picture/{userId} with form data
      const res = await axiosInstance.post(
        `/api/users/edit_profile_picture/${storedUserId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token.trim()}`,
            // Don't set Content-Type - axios will set it automatically as multipart/form-data with boundary
          },
          // Override default Content-Type from axiosInstance config
          transformRequest: (data, headers) => {
            // Remove Content-Type so axios can set it for FormData
            delete headers['Content-Type'];
            return data;
          },
        }
      );

      console.log('Upload response:', res.data);

      if (res.data?.status) {
        toast.success("Profile picture updated successfully!");
        // Refresh profile data to get updated image URL
        await fetchProfileData();
        // Notify Navbar to refresh profile image
        window.dispatchEvent(new Event('profileImageUpdated'));
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Clear preview
        setPreviewImage(null);
        setSelectedFile(null);
      } else {
        console.error('Upload failed - response status:', res.data?.status);
        toast.error(res.data?.message || "Failed to update profile picture");
      }
    } catch (err) {
      console.error('Upload error details:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      toast.error(err.response?.data?.message || err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDownloadIDCard = async () => {
    try {
      toast.loading('Generating ID Card...');
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("userId");

      if (!token || !storedUserId) {
        toast.error('Authentication required');
        return;
      }

      // Fetch fresh user data for ID card
      const response = await axiosInstance.get(`/api/users/${storedUserId}`, {
        headers: { Authorization: `Bearer ${token.trim()}` }
      });

      if (!response.data?.status || !response.data?.data) {
        toast.error('Failed to fetch user data');
        return;
      }

      const userData = response.data.data;

      // Create a temporary container for the card
      const cardContainer = document.createElement('div');
      cardContainer.style.position = 'absolute';
      cardContainer.style.left = '-9999px';
      cardContainer.style.top = '0';
      cardContainer.style.width = '350px';
      cardContainer.style.height = '220px';
      document.body.appendChild(cardContainer);

      // Render the IdentityCard component
      const root = ReactDOM.createRoot(cardContainer);
      root.render(<IdentityCardFormat userData={userData} />);

      // Wait for image to load
      await new Promise(resolve => setTimeout(resolve, 1000));

      const element = cardContainer.querySelector('#identity-card');
      if (!element) {
        toast.error('Failed to generate ID card');
        document.body.removeChild(cardContainer);
        return;
      }

      // Try to use html2pdf if available
      if (window.html2pdf && window.html2pdf().set) {
        const opt = {
          margin: 0,
          filename: `ID_Card_${userData.first_name || 'User'}_${userData.last_name || ''}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: [85.6, 53.98], orientation: 'landscape' }
        };

        await window.html2pdf().set(opt).from(element).save();
        toast.dismiss();
        toast.success('ID Card downloaded successfully!');
      } else {
        // Fallback: Use print dialog with better styling
        const printWindow = window.open('', '_blank');
        const cardHtml = element.outerHTML;
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>ID Card - ${userData.first_name || 'User'} ${userData.last_name || ''}</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                  margin: 0; 
                  padding: 20px; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                  background: #f5f5f5;
                }
                @media print {
                  body { 
                    margin: 0; 
                    padding: 0;
                    background: white;
                  }
                }
                ${document.querySelector('style[data-styled]')?.innerHTML || ''}
              </style>
              <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2/dist/tailwind.min.css" rel="stylesheet">
            </head>
            <body>
              ${cardHtml}
              <script>
                window.onload = function() {
                  setTimeout(function() {
                    window.print();
                  }, 500);
                };
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
        toast.dismiss();
        toast.success('Opening print dialog. Select "Save as PDF" to download.');
      }

      // Cleanup after a delay
      setTimeout(() => {
        if (document.body.contains(cardContainer)) {
          document.body.removeChild(cardContainer);
        }
      }, 2000);
    } catch (err) {
      console.error('Error downloading ID card:', err);
      toast.dismiss();
      toast.error('Failed to download ID card: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar />
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative" style={{ display: 'inline-block' }}>
                <div className="relative" style={{ zIndex: 1 }}>
                  <ProfileImageUpload
                    userId={localStorage.getItem("userId")}
                    token={localStorage.getItem("token")}
                    onImageUpdate={() => {
                      // Refresh profile data after image update
                      fetchProfileData();
                    }}
                  />
                </div>
                {/* Pencil Icon Button for Edit - Positioned outside ProfileImageUpload */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('=== PENCIL BUTTON CLICKED ===');
                    console.log('fileInputRef:', fileInputRef);
                    console.log('fileInputRef.current:', fileInputRef.current);
                    console.log('uploadingImage:', uploadingImage);
                    if (fileInputRef.current) {
                      console.log('Triggering file input click...');
                      try {
                        fileInputRef.current.click();
                        console.log('File input click triggered successfully');
                      } catch (error) {
                        console.error('Error triggering file input:', error);
                        toast.error('Error opening file picker');
                      }
                    } else {
                      console.error('File input ref is null!');
                      toast.error('File input not found. Please refresh the page.');
                    }
                  }}
                  onMouseDown={(e) => {
                    console.log('Pencil button mouse down');
                    e.stopPropagation();
                  }}
                  disabled={uploadingImage}
                  className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 p-2.5 rounded-full shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2 border-white cursor-pointer"
                  style={{
                    zIndex: 10000,
                    transform: 'translate(25%, 25%)',
                    pointerEvents: 'auto',
                    position: 'absolute'
                  }}
                  title="Edit Profile Picture"
                  type="button"
                >
                  {uploadingImage ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Pencil className="h-4 w-4 text-white" />
                  )}
                </button>
                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="hidden"
                  id="profile-image-edit-input"
                />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">My Profile</h1>
                <p className="text-blue-100">View and manage your personal information</p>
              </div>
            </div>

            {/* Buttons aligned to the right */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadIDCard}
                className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-lg"
              >
                <Download size={20} />
                <span>ID Card</span>
              </button>
              <button
                onClick={handleOpenProfileModal}
                className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-lg"
              >
                <Edit size={20} />
                <span>Edit Profile</span>
              </button>
            </div>
          </div>
        </div>

        {/* Profile Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Personal Information</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Full Name</span>
                <span className="font-semibold text-gray-900">{originalProfileData.first_name} {originalProfileData.last_name || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Gender</span>
                <span className="font-semibold text-gray-900 capitalize">{originalProfileData.gender || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Date of Birth</span>
                <span className="font-semibold text-gray-900">{originalProfileData.date_of_birth || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Blood Group</span>
                <span className="font-semibold text-gray-900">{originalProfileData.blood_group || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-500">Country</span>
                <span className="font-semibold text-gray-900">{originalProfileData.country || '-'}</span>
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Contact Information</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Mobile Number</span>
                <span className="font-semibold text-gray-900">{originalProfileData.country_code} {originalProfileData.mobile_number || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Emergency Contact</span>
                <span className="font-semibold text-gray-900">{originalProfileData.emergency_contact_no || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Personal Email</span>
                <span className="font-semibold text-gray-900">{originalProfileData.personal_email || '-'}</span>
              </div>
            </div>
          </div>

          {/* Family Information Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Family Information</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Father's Name</span>
                <span className="font-semibold text-gray-900">{originalProfileData.father_name || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-500">Mother's Name</span>
                <span className="font-semibold text-gray-900">{originalProfileData.mother_name || '-'}</span>
              </div>
            </div>
          </div>

          {/* Identity Documents Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Identity Documents</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">PAN Card</span>
                <span className="font-semibold text-gray-900">{originalProfileData.pan_card_number || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Aadhar Number</span>
                <span className="font-semibold text-gray-900">{originalProfileData.aadhar_number || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-500">Name on Aadhar</span>
                <span className="font-semibold text-gray-900">{originalProfileData.aadhar_name || '-'}</span>
              </div>
            </div>
          </div>

          {/* Bank Details Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-rose-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Bank Details</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Bank Name</span>
                <span className="font-semibold text-gray-900">{originalProfileData.bank_name || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Account Number</span>
                <span className="font-semibold text-gray-900">{originalProfileData.account_number ? '****' + originalProfileData.account_number.slice(-4) : '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-500">IFSC Code</span>
                <span className="font-semibold text-gray-900">{originalProfileData.ifsc_code || '-'}</span>
              </div>
            </div>
          </div>

          {/* Address Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-teal-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Address</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-500 text-sm">Permanent Address</span>
                <p className="font-semibold text-gray-900 mt-1">{originalProfileData.permanent_address || '-'}</p>
              </div>
              <div className="py-2">
                <span className="text-gray-500 text-sm">Current Address</span>
                <p className="font-semibold text-gray-900 mt-1">{originalProfileData.current_address || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Update Modal */}
      {showProfileModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking backdrop (only if profile is completed)
            if (e.target === e.currentTarget && localStorage.getItem('profileCompleted')) {
              setShowProfileModal(false);
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">Update Profile</h2>
              <button
                onClick={() => {
                  // Only allow closing if profile is completed or user confirms
                  const profileCompleted = localStorage.getItem('profileCompleted');
                  if (profileCompleted) {
                    setShowProfileModal(false);
                  } else {
                    // For first-time users, show confirmation
                    if (window.confirm('Are you sure you want to close? Your profile information will not be saved.')) {
                      setShowProfileModal(false);
                    }
                  }
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                title="Close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleProfileSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              {/* Personal Information */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b flex items-center gap-2">
                  <User size={20} className="text-blue-600" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name {hasDbValue('first_name') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="first_name" value={profileData.first_name} onChange={handleProfileChange}
                      className={getInputClass('first_name')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name {hasDbValue('last_name') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="last_name" value={profileData.last_name} onChange={handleProfileChange}
                      className={getInputClass('last_name')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Gender {hasDbValue('gender') && <span className="text-green-500">✓</span>}
                    </label>
                    <select name="gender" value={profileData.gender} onChange={handleProfileChange}
                      className={getInputClass('gender')}>
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date of Birth {hasDbValue('date_of_birth') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="date" name="date_of_birth" value={profileData.date_of_birth} onChange={handleProfileChange}
                      className={getInputClass('date_of_birth')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Blood Group {hasDbValue('blood_group') && <span className="text-green-500">✓</span>}
                    </label>
                    <select name="blood_group" value={profileData.blood_group} onChange={handleProfileChange}
                      className={getInputClass('blood_group')}>
                      <option value="">Select</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Country {hasDbValue('country') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="country" value={profileData.country} onChange={handleProfileChange}
                      className={getInputClass('country')} />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b flex items-center gap-2">
                  <Phone size={20} className="text-green-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mobile Number {hasDbValue('mobile_number') && <span className="text-green-500">✓</span>}
                    </label>
                    <div className="flex gap-2">
                      <input type="text" name="country_code" value={profileData.country_code} onChange={handleProfileChange}
                        className={`w-20 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasDbValue('country_code') ? 'border-green-400 bg-green-50' : 'border-gray-300'}`} placeholder="+91" />
                      <input type="text" name="mobile_number" value={profileData.mobile_number} onChange={handleProfileChange}
                        className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasDbValue('mobile_number') ? 'border-green-400 bg-green-50' : 'border-gray-300'}`} placeholder="Enter mobile number" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Emergency Contact {hasDbValue('emergency_contact_no') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="emergency_contact_no" value={profileData.emergency_contact_no} onChange={handleProfileChange}
                      className={getInputClass('emergency_contact_no')} placeholder="Enter emergency contact" />
                  </div>
                </div>
              </div>

              {/* Family Information */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b flex items-center gap-2">
                  <Users size={20} className="text-purple-600" />
                  Family Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Father's Name {hasDbValue('father_name') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="father_name" value={profileData.father_name} onChange={handleProfileChange}
                      className={getInputClass('father_name')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mother's Name {hasDbValue('mother_name') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="mother_name" value={profileData.mother_name} onChange={handleProfileChange}
                      className={getInputClass('mother_name')} />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b flex items-center gap-2">
                  <MapPin size={20} className="text-teal-600" />
                  Address Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Permanent Address {hasDbValue('permanent_address') && <span className="text-green-500">✓</span>}
                    </label>
                    <textarea name="permanent_address" value={profileData.permanent_address} onChange={handleProfileChange}
                      className={getInputClass('permanent_address')} rows="3" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Address {hasDbValue('current_address') && <span className="text-green-500">✓</span>}
                    </label>
                    <textarea name="current_address" value={profileData.current_address} onChange={handleProfileChange}
                      className={getInputClass('current_address')} rows="3" />
                  </div>
                </div>
              </div>

              {/* Identity Documents */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b flex items-center gap-2">
                  <FileText size={20} className="text-amber-600" />
                  Identity Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      PAN Card Number {hasDbValue('pan_card_number') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="pan_card_number" value={profileData.pan_card_number} onChange={handleProfileChange}
                      className={getInputClass('pan_card_number')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Aadhar Number {hasDbValue('aadhar_number') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="aadhar_number" value={profileData.aadhar_number} onChange={handleProfileChange}
                      className={getInputClass('aadhar_number')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name on Aadhar {hasDbValue('aadhar_name') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="aadhar_name" value={profileData.aadhar_name} onChange={handleProfileChange}
                      className={getInputClass('aadhar_name')} />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b flex items-center gap-2">
                  <CreditCard size={20} className="text-rose-600" />
                  Bank Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Bank Name {hasDbValue('bank_name') && <span className="text-green-500">✓</span>}
                    </label>
                    <div
                      onClick={() => setShowBankDropdown(!showBankDropdown)}
                      className={`w-full px-4 py-3 border rounded-lg cursor-pointer bg-white flex items-center gap-2 ${hasDbValue('bank_name') ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
                    >
                      {profileData.bank_name ? (
                        <>
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: INDIAN_BANKS.find(b => b.name === profileData.bank_name)?.color || '#6b7280' }}
                          >
                            {profileData.bank_name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                          </div>
                          <span>{profileData.bank_name}</span>
                        </>
                      ) : (
                        <span className="text-gray-400">Select Bank</span>
                      )}
                    </div>
                    {showBankDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {INDIAN_BANKS.map((bank) => (
                          <div
                            key={bank.name}
                            onClick={() => {
                              setProfileData(prev => ({ ...prev, bank_name: bank.name }));
                              setShowBankDropdown(false);
                            }}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer"
                          >
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ backgroundColor: bank.color }}
                            >
                              {bank.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                            </div>
                            <span>{bank.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Account Number {hasDbValue('account_number') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="account_number" value={profileData.account_number} onChange={handleProfileChange}
                      className={getInputClass('account_number')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Account Number</label>
                    <input type="text" name="confirm_account_number" value={profileData.confirm_account_number} onChange={handleProfileChange}
                      className={getInputClass('confirm_account_number')} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      IFSC Code {hasDbValue('ifsc_code') && <span className="text-green-500">✓</span>}
                    </label>
                    <input type="text" name="ifsc_code" value={profileData.ifsc_code} onChange={handleProfileChange}
                      className={getInputClass('ifsc_code')} />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    const profileCompleted = localStorage.getItem('profileCompleted');
                    if (profileCompleted) {
                      setShowProfileModal(false);
                    } else {
                      if (window.confirm('Are you sure you want to cancel? Your profile information will not be saved.')) {
                        setShowProfileModal(false);
                      }
                    }
                  }}
                  disabled={profileLoading}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={profileLoading}
                  className={`px-6 py-3 rounded-lg text-white font-medium transition-colors shadow-md hover:shadow-lg ${profileLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'}`}
                >
                  {profileLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
